import _extends from '../../../node_modules/@babel/runtime/helpers/extends.js';
import _objectWithoutProperties from '../../../node_modules/@babel/runtime/helpers/objectWithoutProperties.js';
import _defineProperty from '../../../node_modules/@babel/runtime/helpers/defineProperty.js';
import React, { useMemo } from 'react';
import 'prop-types';
import { jsx } from '@emotion/core';
import merge from 'deepmerge';
import select from '../../styles/select.js';
import forms from '../../styles/forms.js';
import { label1 } from '../../styles/typography/label1.js';
import { fluid } from '../../styles/layout/fluid.js';
import { dlsGray05 } from '../../styles/colorsDls/dlsGray05.js';
import { dlsGray03 } from '../../styles/colorsDls/dlsGray03.js';
import { focusDark } from '../../styles/focus/focusDark.js';
import GlyphDown from '../../svg/GlyphDown.js';
import getPreferredValue from '../../utils/getPreferredValue.js';
import { useFieldControlContext } from '../FieldControl/FieldControlContext.js';
import { useThemeContext } from '../ThemeProvider/ThemeContext.js';
import ReactlyticsTracking from '../ReactlyticsTracking.js';
import noop from '../../utils/noop.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var iconObject = _objectSpread(_objectSpread({}, select.select['&::after']), dlsGray05);

var Select = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
      id = _ref.id,
      value = _ref.value,
      fluid$1 = _ref.fluid,
      twoLined = _ref.twoLined,
      disabled = _ref.disabled,
      required = _ref.required,
      status = _ref.status,
      theme = _ref.theme,
      children = _ref.children,
      onChange = _ref.onChange,
      onBlur = _ref.onBlur,
      onFocus = _ref.onFocus,
      reactlytics = _ref.reactlytics,
      otherProps = _objectWithoutProperties(_ref, ["className", "id", "value", "fluid", "twoLined", "disabled", "required", "status", "theme", "children", "onChange", "onBlur", "onFocus", "reactlytics"]);

  var fieldControlContext = useFieldControlContext();
  var themeTokens = useThemeContext();

  var _ref2 = fieldControlContext || {},
      contextStatus = _ref2.contextStatus,
      handleFieldTouch = _ref2.handleFieldTouch;

  var handleSelectBlur = function handleSelectBlur(event) {
    if (handleFieldTouch) {
      handleFieldTouch(event.target.value, required);
    }

    onBlur(event);
  };

  var formControlStyle = useMemo(function () {
    return merge(forms.formControl, themeTokens.primaryColor ? {
      '&:active': {
        borderColor: themeTokens.primaryColor
      },
      '&:focus': {
        borderColor: themeTokens.primaryColor
      }
    } : {});
  }, [themeTokens]);
  var selectObject = useMemo(function () {
    var selectStyle = _objectSpread(_objectSpread({}, select.select_select), {
      '&:focus': _objectSpread({}, focusDark)
    });

    if (twoLined) {
      selectStyle = _objectSpread(_objectSpread({}, selectStyle), select.selectTwoLined_formControl_select);
    }

    return selectStyle;
  }, [twoLined]);
  var selectStatus = getPreferredValue(contextStatus, status);
  var styleObject = useMemo(function () {
    var styleObj = _objectSpread(_objectSpread(_objectSpread({}, formControlStyle), select.select_formControl), {}, {
      maxWidth: '18.75rem',
      position: 'relative',
      marginTop: '0.3125rem'
    }, theme);

    if (selectStatus === 'error') {
      styleObj = merge.all([styleObj, select.hasWarning__select_formControl, select.select_formControlWarning, {
        '& > select': _objectSpread({}, select.select_formControlWarning_select)
      }]);
    }

    if (fluid$1) {
      styleObj = merge.all([styleObj, fluid]);
    }

    if (disabled) {
      styleObj = merge.all([styleObj, select.select_disabled, forms.formControl__disabled]);
    }

    if (twoLined) {
      styleObj = _objectSpread(_objectSpread({}, styleObj), select.selectTwoLined_formControl);
    } else {
      styleObj = _objectSpread(_objectSpread({}, styleObj), {}, {
        height: '3.125rem'
      });
    }

    return styleObj;
  }, [disabled, fluid$1, formControlStyle, selectStatus, theme, twoLined]);
  var dataLabel = useMemo(function () {
    var label = '';

    if (twoLined) {
      // More than one option
      if (Array.isArray(children)) {
        children.forEach(function (child) {
          if (value === child.props.value) {
            label = child.props['data-label'];
          }
        });
      } else {
        // One option
        label = children.props['data-label'];
      }
    }

    return label;
  }, [value, twoLined, children]);
  var glyphTheme = useMemo(function () {
    return merge.all([iconObject, disabled ? dlsGray03 : {}]);
  }, [disabled]);
  return jsx("div", {
    css: styleObject,
    className: className
  }, twoLined && // eslint-disable-next-line jsx-a11y/label-has-associated-control
  jsx("label", {
    css: _objectSpread(_objectSpread({}, label1), select.selectTwoLined_formControl_label)
  }, dataLabel), jsx(ReactlyticsTracking, {
    onFocus: onFocus,
    onBlur: handleSelectBlur,
    onChange: onChange,
    reactlytics: reactlytics
  }, jsx("select", _extends({
    id: id,
    css: selectObject,
    disabled: disabled,
    required: required,
    value: value // eslint-disable-next-line react/jsx-props-no-spreading

  }, otherProps, {
    ref: ref
  }), children)), jsx(GlyphDown, {
    theme: glyphTheme
  }));
});
Select.displayName = 'Select';
Select.defaultProps = {
  className: '',
  value: undefined,
  fluid: false,
  twoLined: false,
  disabled: false,
  required: false,
  status: 'default',
  theme: undefined,
  onChange: noop,
  onBlur: noop,
  onFocus: noop
};

export default Select;
